import { Box } from "@mui/material";
import { Typography } from "@platform/design-system";

import { CARD_SX, TIER_COLORS, TIER_ORDER } from "./constants";
import { HorizontalBarChart } from "./HorizontalBarChart";
import { CardTitle } from "./primitives";

import type { CarrierTier } from "../types";

type CarrierOutcomeDatum = { name: string; amount: number; tier: CarrierTier };

type CarrierOutcomesCardProps = {
  premiumWon: CarrierOutcomeDatum[];
  premiumRetained: CarrierOutcomeDatum[];
};

function CarrierOutcomesCard({
  premiumWon,
  premiumRetained,
}: CarrierOutcomesCardProps): React.JSX.Element {
  return (
    <Box
      data-testid="carrier-outcomes-card"
      sx={{
        ...CARD_SX,
        display: "flex",
        flex: 1,
        flexDirection: "column",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          columnGap: 2,
          rowGap: 1,
          mb: 2,
        }}
      >
        <CardTitle>Carrier Outcomes by Premium</CardTitle>
        <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
          {TIER_ORDER.map((tier) => (
            <Box
              key={tier}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor: TIER_COLORS[tier],
                }}
              />
              <Typography variant="caption" color="textSecondary">
                {tier}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 3,
          alignItems: "stretch",
          flex: 1,
          minHeight: 0,
          "@media (max-width: 1024px)": {
            gridTemplateColumns: "1fr",
          },
        }}
      >
        <HorizontalBarChart
          title="New Premium Sold"
          description="Shows premium awarded to each carrier, coloured by tier (Tier 1, Tier 2, or Non-Strategic). Includes new business with no prior incumbent carrier, plus existing coverage moved from one carrier to another."
          data={premiumWon}
        />
        <HorizontalBarChart
          title="Retained Premium"
          description="Shows premium kept with each incumbent carrier, coloured by tier (Tier 1, Tier 2, or Non-Strategic), where the incumbent carrier was selected again at the current premium for that coverage."
          data={premiumRetained}
        />
      </Box>
    </Box>
  );
}

export { CarrierOutcomesCard };
